import { IContextualMenuItem } from 'office-ui-fabric-react/lib/components/ContextualMenu/index';
import { ClientId, IEngagementExtraData, ISharingStore } from '../interfaces/SharingInterfaces';
import stringsResx from '../components/Share.resx';
import * as ClientIdHelper from '../utilities/ClientIdHelper';
import { ISharingItemInformation } from '../interfaces/SharingInterfaces';

module AttachAsCopyHelper {
    /**
     * This function returns available "Attach as Copy" options for use
     * when the UI is hosted by native Office clients.
     *
     * @param clientId The clientId of the hosting application.
     * @param item The item information (e.g. name)
     * @param sharingStore An instance of the sharing store
     * @param engagementExtraData Adding data for engagement events
     */
    export function getAttachAsCopyOptions(
        clientId: ClientId,
        item: ISharingItemInformation,
        sharingStore: ISharingStore,
        engagementExtraData: IEngagementExtraData): Array<IContextualMenuItem> {
        const options = [];

        // Get document option (i.e. Word, Excel, PowerPoint) and add if available.
        const documentOption = _getDocumentOption(sharingStore, clientId, item, engagementExtraData);
        if (documentOption) {
            options.push(documentOption);
        }

        // Get PDF option and add if available.
        const pdfOption = _getPdfOption(sharingStore, engagementExtraData);
        if (pdfOption) {
            options.push(pdfOption);
        }

        return options;
    }

    function _getDocumentOption(sharingStore: ISharingStore, clientId: ClientId, item: ISharingItemInformation, engagementExtraData: IEngagementExtraData): IContextualMenuItem {
        let isDocumentOptionAvailable = false;

        let fileName = item.name.split('.');
        let fileExtension = '';
        if (fileName.length > 1) {
            fileExtension = fileName[1].toLowerCase();
        }

        // Determine if attaching as document is supported.
        try {
            const externalJavaScript: any = window.external;
            isDocumentOptionAvailable = externalJavaScript.IsSendCopyEnabled();
        } catch (error) {
            // Nothing.
        }

        // If unavailable, just return.
        if (!isDocumentOptionAvailable) {
            return undefined;
        }

        const externalSendCopy = () => {
            _externalSendCopy(sharingStore, engagementExtraData);
        };

        // Return appropriate option based on clientId.
        if (ClientIdHelper.isWord(clientId) || _isWordFile(fileExtension)) {
            return {
                key: 'word',
                name: stringsResx.wordDocument,
                iconProps: {
                    iconName: 'WordDocument',
                    className: 'Share-wordIcon'
                },
                onClick: externalSendCopy
            };
        } else if (ClientIdHelper.isPowerPoint(clientId) || _isPowerPointFile(fileExtension)) {
            return {
                key: 'powerpoint',
                name: stringsResx.powerPointPresentation,
                iconProps: {
                    iconName: 'PowerPointDocument',
                    className: 'Share-powerpointIcon'
                },
                onClick: externalSendCopy
            };
        } else if (ClientIdHelper.isExcel(clientId) || _isExcelFile(fileExtension)) {
            return {
                key: 'excel',
                name: stringsResx.excelWorkbook,
                iconProps: {
                    iconName: 'ExcelDocument',
                    className: 'Share-excelIcon'
                },
                onClick: externalSendCopy
            };
        } else if (ClientIdHelper.isVisio(clientId) || _isVisioFile(fileExtension)) {
            return {
                key: 'visio',
                name: stringsResx.visioDrawing,
                iconProps: { iconName: 'VisioLogo' },
                onClick: externalSendCopy
            };
        } else {
            return {
                key: 'document',
                name: stringsResx.document,
                iconProps: { iconName: 'Document' },
                onClick: externalSendCopy
            };
        }
    }

    function _isWordFile(fileExtension: string) {
        const wordFileExtensions = ['doc', 'docx', 'dot', 'docm', 'odt', 'rtf', 'txt'];
        return wordFileExtensions.indexOf(fileExtension) > -1;
    }

    function _isPowerPointFile(fileExtension: string) {
        const powerPointExtensions = ['ppt', 'pptx', 'pptm', 'ppsx', 'pps', 'ppsm', 'odp'];
        return powerPointExtensions.indexOf(fileExtension) > -1;
    }

    function _isExcelFile(fileExtension: string) {
        const excelExtensions = ['xlsx', 'xlsm', 'xlsb', 'xls', 'ods', 'csv', 'xlt', 'xltm', 'xltx'];
        return excelExtensions.indexOf(fileExtension) > -1;
    }

    function _isVisioFile(fileExtension: string) {
        const visioExtensions = ['vsd', 'vsdx']
        return visioExtensions.indexOf(fileExtension) > -1;
    }

    function _getPdfOption(sharingStore: ISharingStore, engagementExtraData: IEngagementExtraData): IContextualMenuItem {
        let isPdfOptionAvailable = false;

        // Determine if attaching as PDF is supported.
        try {
            const externalJavaScript: any = window.external;
            isPdfOptionAvailable = externalJavaScript.IsSendPdfEnabled();
        } catch (error) {
            // Nothing.
        }

        // If unavailable, just return.
        if (!isPdfOptionAvailable) {
            return undefined;
        }

        return {
            key: 'pdf',
            name: stringsResx.pdf,
            iconProps: {
                iconName: 'PDF',
                className: 'Share-pdfIcon'
            },
            onClick: () => {
                _externalSendPdf(sharingStore, engagementExtraData);
            }
        };
    }

    function _externalSendCopy(sharingStore: ISharingStore, engagementExtraData: IEngagementExtraData) {
        sharingStore.getEngagementHelper().sendACopyOfficeClicked(engagementExtraData);
        try {
            const externalJavaScript: any = window.external;
            externalJavaScript.SendCopy();
        } catch (error) {
            // Nothing.
        }
    }

    function _externalSendPdf(sharingStore: ISharingStore, engagementExtraData: IEngagementExtraData) {
        sharingStore.getEngagementHelper().sendACopyPdfClicked(engagementExtraData);
        try {
            const externalJavaScript: any = window.external;
            externalJavaScript.SendPdf();
        } catch (error) {
            // Nothing.
        }
    }
}

export default AttachAsCopyHelper;